import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  labelClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getMswMissedVisitForm,
  updateMswMissedVisitForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CalendarIcon,
  CheckCircleIcon,
  CommentsIcon,
  HelpingIcon,
  PhoneIcon,
  PlayIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  MSW_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ATTEMPT_COUNT_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  COORDINATION_DISCIPLINE_OPTIONS,
  MISSED_REASON_OPTIONS,
  MSW_MISSED_ARRAY_KEYS,
  YES_NO_OPTIONS,
  emptyMswMissedVisitPayload,
  toggleInList,
  today,
} from "./mswMissedVisitContent";
import MswNotesCommonHeader from "./MswNotesCommonHeader";
import {
  normalizeMswCommonHeaderState,
  resolveMswNotesCommonHeader,
  withNormalizedMswCommonHeader,
} from "./mswNotesCommonHeaderUtils";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "../nurseNotes/nurseVisitCommon";

const MISSED_VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const normalizeExclusiveYesNo = (value) => {
  if (Array.isArray(value)) {
    if (value.includes("Yes")) return "Yes";
    if (value.includes("No")) return "No";
    return "";
  }
  const s = String(value || "").trim();
  if (s === "Yes" || s === "No") return s;
  return "";
};

const normalizeAttemptCount = (value) => {
  const s = String(value || "").trim();
  return ATTEMPT_COUNT_OPTIONS.includes(s) ? s : "";
};

const CONTACT_METHOD_ICONS = {
  "Phone Call": PhoneIcon,
  "SMS/Text": CommentsIcon,
  "Voicemail Left": PlayIcon,
};

const toBool = (value) => value === true || value === "true" || value === 1;

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      compact
      className="min-w-0 w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const NotifyRow = ({
  label,
  checked,
  dateValue,
  timeValue,
  dateName,
  timeName,
  canEdit,
  printMode,
  onCheckedChange,
  onDateChange,
  onTimeChange,
}) => (
  <div className="grid grid-cols-1 sm:grid-cols-[minmax(11rem,1.2fr)_minmax(9.5rem,0.9fr)_minmax(7rem,0.7fr)] gap-x-3 gap-y-1 items-end border-b border-slate-100 last:border-b-0 py-1.5">
    <label className="inline-flex items-center gap-2 min-h-7 text-[12px] font-medium text-slate-700">
      <input
        type="checkbox"
        className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        checked={Boolean(checked)}
        disabled={!canEdit}
        onChange={(e) => onCheckedChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
    <div className="min-w-0">
      <span className={labelClass}>Date</span>
      <DateFieldInput
        name={dateName}
        value={dateValue}
        canEdit={canEdit && Boolean(checked)}
        printMode={printMode}
        onChange={onDateChange}
      />
    </div>
    <div className="min-w-0">
      <span className={labelClass}>Time</span>
      {printMode ? (
        <div className={`${fieldClass} flex items-center`}>
          {timeValue || "—"}
        </div>
      ) : (
        <input
          type="time"
          name={timeName}
          className={fieldClass}
          value={timeValue || ""}
          disabled={!canEdit || !checked}
          onChange={(e) => onTimeChange(e.target.value)}
        />
      )}
    </div>
  </div>
);

NotifyRow.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  dateValue: PropTypes.string,
  timeValue: PropTypes.string,
  dateName: PropTypes.string.isRequired,
  timeName: PropTypes.string.isRequired,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onCheckedChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
};

const emptyValues = () => ({
  ...emptyMswMissedVisitPayload(),
  missedVisitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const MswMissedVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyMswMissedVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickNurseVisitMeta(loadedPayload, data),
        missedVisitDate:
          data.missedVisitDate || loadedPayload.missedVisitDate || today(),
        commonHeader: normalizeMswCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        attemptsCount: normalizeAttemptCount(loadedPayload.attemptsCount),
        patientAwareOfNextVisit: normalizeExclusiveYesNo(
          loadedPayload.patientAwareOfNextVisit,
        ),
        reasonOtherSpecify: String(loadedPayload.reasonOtherSpecify || "").trim(),
        interventionsGoalsAffected: String(
          loadedPayload.interventionsGoalsAffected || "",
        ),
        functionalImpact: String(loadedPayload.functionalImpact || ""),
        physicianNotified: toBool(loadedPayload.physicianNotified),
        physicianNotifiedDate: loadedPayload.physicianNotifiedDate || "",
        physicianNotifiedTime: loadedPayload.physicianNotifiedTime || "",
        familyNotified: toBool(loadedPayload.familyNotified),
        familyNotifiedDate: loadedPayload.familyNotifiedDate || "",
        familyNotifiedTime: loadedPayload.familyNotifiedTime || "",
        caseManagerNotified: toBool(loadedPayload.caseManagerNotified),
        caseManagerNotifiedDate: loadedPayload.caseManagerNotifiedDate || "",
        caseManagerNotifiedTime: loadedPayload.caseManagerNotifiedTime || "",
        rescheduledDate: loadedPayload.rescheduledDate || "",
        nextScheduledFrequency: loadedPayload.nextScheduledFrequency || "",
        clinicalStrategy: loadedPayload.clinicalStrategy || "",
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      MSW_MISSED_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveMswNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "MSW Missed Visit",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            missedVisitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyMswMissedVisitPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getMswMissedVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveMswNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        MSW_FORM_EDITOR_ROLES,
        "View only — only MSW can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      missedVisitDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...mswFields
    } = values;

    const nestedPayload = withNormalizedMswCommonHeader({
      ...mswFields,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : "",
      attemptsCount: normalizeAttemptCount(values.attemptsCount),
      patientAwareOfNextVisit: normalizeExclusiveYesNo(
        values.patientAwareOfNextVisit,
      ),
      reasonOtherSpecify: String(values.reasonOtherSpecify || "").trim(),
      interventionsGoalsAffected: String(
        values.interventionsGoalsAffected || "",
      ).trim(),
      functionalImpact: String(values.functionalImpact || "").trim(),
      physicianNotified: toBool(values.physicianNotified),
      physicianNotifiedDate: values.physicianNotifiedDate
        ? formatDateForInput(values.physicianNotifiedDate)
        : "",
      physicianNotifiedTime: values.physicianNotifiedTime || "",
      familyNotified: toBool(values.familyNotified),
      familyNotifiedDate: values.familyNotifiedDate
        ? formatDateForInput(values.familyNotifiedDate)
        : "",
      familyNotifiedTime: values.familyNotifiedTime || "",
      caseManagerNotified: toBool(values.caseManagerNotified),
      caseManagerNotifiedDate: values.caseManagerNotifiedDate
        ? formatDateForInput(values.caseManagerNotifiedDate)
        : "",
      caseManagerNotifiedTime: values.caseManagerNotifiedTime || "",
      rescheduledDate: values.rescheduledDate
        ? formatDateForInput(values.rescheduledDate)
        : "",
      nextScheduledFrequency: String(values.nextScheduledFrequency || "").trim(),
      clinicalStrategy: String(values.clinicalStrategy || "").trim(),
    });

    MSW_MISSED_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateMswMissedVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.missedVisitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showReasonOther = (values.missedReasons || []).includes("Other");

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            MSW Missed Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home health clinical documentation for missed medical social work
            visits.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Missed Visit Date"
          dateValue={displayDateUS(values.missedVisitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Missed Visit Date
                </div>
                <InputDatePicker
                  name="missedVisitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.missedVisitDate) || ""}
                  onChange={(event) =>
                    setValue("missedVisitDate", event.target.value)
                  }
                  max={MISSED_VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <MswNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={AlertCircleIcon} title="7. Reason for Missed Visit">
          <CheckboxGrid
            options={MISSED_REASON_OPTIONS}
            values={values.missedReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("missedReasons", opt)}
            columns={3}
          />
          {showReasonOther ? (
            <div className="mt-3">
              <Field label="Other (Please Specify)">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.reasonOtherSpecify || ""}
                  disabled={!canEdit}
                  placeholder="Specify other reason…"
                  onChange={(e) =>
                    setValue("reasonOtherSpecify", e.target.value)
                  }
                />
              </Field>
            </div>
          ) : null}
        </Section>

        <Section icon={PhoneIcon} title="8. Attempts Made">
          <div className="grid grid-cols-1 lg:grid-cols-[9.5rem_minmax(0,1fr)] gap-x-4 gap-y-2 items-end">
            <div>
              <SubHeading>Number of Attempts</SubHeading>
              <div className="mt-1 flex h-8 overflow-hidden rounded-md border border-slate-200 bg-white">
                {ATTEMPT_COUNT_OPTIONS.map((opt, index) => {
                  const selected = values.attemptsCount === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={!canEdit}
                      onClick={() =>
                        setValue("attemptsCount", selected ? "" : opt)
                      }
                      className={`h-full min-w-0 flex-1 px-2 text-[11px] font-semibold transition-colors ${
                        index > 0 ? "border-l border-slate-200" : ""
                      } ${
                        selected
                          ? "bg-indigo-600 text-white"
                          : "bg-white text-slate-700 hover:bg-indigo-50"
                      } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="min-w-0">
              <SubHeading>Methods of Communication</SubHeading>
              <div className="mt-1 grid h-8 grid-cols-3 gap-1.5">
                {CONTACT_METHOD_OPTIONS.map((opt) => {
                  const selected = (values.contactMethods || []).includes(opt);
                  const Icon = CONTACT_METHOD_ICONS[opt] || PhoneIcon;
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => toggleListValue("contactMethods", opt)}
                      className={`inline-flex h-full min-w-0 items-center gap-1.5 rounded-md border px-2 text-left transition-colors ${
                        selected
                          ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                          : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
                      } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
                    >
                      <Icon
                        size={13}
                        className={`shrink-0 ${selected ? "text-indigo-600" : "text-slate-500"}`}
                      />
                      <span className="min-w-0 flex-1 truncate text-[11px] font-semibold leading-none">
                        {opt}
                      </span>
                      {selected ? (
                        <CheckCircleIcon
                          size={13}
                          className="shrink-0 text-indigo-600"
                          aria-hidden
                        />
                      ) : (
                        <span
                          className="inline-block h-3 w-3 shrink-0 rounded-full border border-slate-300"
                          aria-hidden
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>

        <Section icon={TargetIcon} title="9. Impact on Plan of Care">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Field label="Interventions / Goals Affected">
              <textarea
                rows={5}
                className={textareaClass}
                value={values.interventionsGoalsAffected || ""}
                disabled={!canEdit}
                placeholder="Describe interventions or goals affected by the missed visit…"
                onChange={(e) =>
                  setValue("interventionsGoalsAffected", e.target.value)
                }
              />
            </Field>
            <Field label="Functional Impact">
              <textarea
                rows={5}
                className={textareaClass}
                value={values.functionalImpact || ""}
                disabled={!canEdit}
                placeholder="Describe impact on the patient's functional status and plan of care…"
                onChange={(e) => setValue("functionalImpact", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3 rounded-md border border-indigo-100 bg-indigo-50/60 px-3 py-2.5">
            <SubHeading>
              Was patient made aware of next scheduled visit?
            </SubHeading>
            <div className="mt-1.5">
              <ExclusiveChoiceGrid
                name="patientAwareOfNextVisit"
                options={YES_NO_OPTIONS}
                value={values.patientAwareOfNextVisit || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("patientAwareOfNextVisit", opt)}
                columns={2}
              />
            </div>
          </div>
        </Section>

        <Section icon={HelpingIcon} title="10. Coordination & Notifications">
          <div className="rounded-md border border-slate-200 bg-white px-3 py-0.5">
            <NotifyRow
              label="Physician notified"
              checked={values.physicianNotified}
              dateValue={values.physicianNotifiedDate}
              timeValue={values.physicianNotifiedTime}
              dateName="physicianNotifiedDate"
              timeName="physicianNotifiedTime"
              canEdit={canEdit}
              printMode={printMode}
              onCheckedChange={(v) => setValue("physicianNotified", v)}
              onDateChange={(v) => setValue("physicianNotifiedDate", v)}
              onTimeChange={(v) => setValue("physicianNotifiedTime", v)}
            />
            <NotifyRow
              label="Family / Caregiver notified"
              checked={values.familyNotified}
              dateValue={values.familyNotifiedDate}
              timeValue={values.familyNotifiedTime}
              dateName="familyNotifiedDate"
              timeName="familyNotifiedTime"
              canEdit={canEdit}
              printMode={printMode}
              onCheckedChange={(v) => setValue("familyNotified", v)}
              onDateChange={(v) => setValue("familyNotifiedDate", v)}
              onTimeChange={(v) => setValue("familyNotifiedTime", v)}
            />
            <NotifyRow
              label="Case Manager notified"
              checked={values.caseManagerNotified}
              dateValue={values.caseManagerNotifiedDate}
              timeValue={values.caseManagerNotifiedTime}
              dateName="caseManagerNotifiedDate"
              timeName="caseManagerNotifiedTime"
              canEdit={canEdit}
              printMode={printMode}
              onCheckedChange={(v) => setValue("caseManagerNotified", v)}
              onDateChange={(v) => setValue("caseManagerNotifiedDate", v)}
              onTimeChange={(v) => setValue("caseManagerNotifiedTime", v)}
            />
          </div>
        </Section>

        <Section icon={CalendarIcon} title="11. Follow-up Plan">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Rescheduled Visit Date">
              <DateFieldInput
                name="rescheduledDate"
                value={values.rescheduledDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("rescheduledDate", v)}
              />
            </Field>
            <Field label="Next Scheduled Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.nextScheduledFrequency || ""}
                disabled={!canEdit}
                placeholder="e.g. 1x/week"
                onChange={(e) =>
                  setValue("nextScheduledFrequency", e.target.value)
                }
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Clinical Strategy">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.clinicalStrategy || ""}
                disabled={!canEdit}
                placeholder="Plan to address the missed visit and prevent recurrence…"
                onChange={(e) => setValue("clinicalStrategy", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Coordination with Other Disciplines</SubHeading>
            <div className="mt-1.5">
              <CheckboxGrid
                options={COORDINATION_DISCIPLINE_OPTIONS}
                values={values.coordinationDisciplines}
                canEdit={canEdit}
                onToggle={(opt) =>
                  toggleListValue("coordinationDisciplines", opt)
                }
                columns={3}
              />
            </div>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
          clinicianSectionTitle="Authentication & Certification"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

MswMissedVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default MswMissedVisitForm;
